// src/pages/DeviceManagement/components/SearchBar/UnifiedSearch.jsx
// Unified search component for devices, locations, IPs, and MACs
// Part of Device Management Overhaul - Phase 2

import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Fade,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Router as DeviceIcon,
  LocationOn as LocationIcon,
  NetworkCheck as IPIcon,
  Memory as MACIcon,
  History as HistoryIcon
} from '@mui/icons-material';

const UnifiedSearch = memo(({
  onSearch,
  totalDevices = 0,
  filteredCount = 0,
  isLoading = false,
  placeholder = "Search devices, locations, IPs, MACs...",
  showSuggestions = true,
  recentSearches = [],
  onClearRecentSearches,
  autoFocus = false
}) => {
  const [localSearch, setLocalSearch] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [searchHistory, setSearchHistory] = useState(recentSearches);
  const [suggestions, setSuggestions] = useState([]);
  const searchInputRef = useRef(null);
  const searchTimeout = useRef(null);

  // Load search history from localStorage
  useEffect(() => {
    const history = localStorage.getItem('deviceSearch.history');
    if (history) {
      try {
        setSearchHistory(JSON.parse(history));
      } catch (e) {
        console.error('Error loading search history:', e);
      }
    }
  }, []);

  // Debounced search execution - optimized to reduce re-renders
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set new timeout for search
    searchTimeout.current = setTimeout(() => {
      if (onSearch) {
        onSearch(localSearch);
      }
    }, 300);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [localSearch, onSearch]);

  // Save to history only when search is submitted (on blur or enter)
  // This prevents localStorage writes on every keystroke
  const saveToHistory = useCallback((term) => {
    if (term && !searchHistory.includes(term)) {
      const newHistory = [term, ...searchHistory].slice(0, 5);
      setSearchHistory(newHistory);
      localStorage.setItem('deviceSearch.history', JSON.stringify(newHistory));
    }
  }, [searchHistory]);

  // Generate search suggestions based on input
  useEffect(() => {
    if (localSearch && showSuggestions) {
      generateSuggestions(localSearch);
    } else {
      setSuggestions([]);
    }
  }, [localSearch, showSuggestions]);

  const generateSuggestions = (term) => {
    const suggestions = [];

    // Detect search type and provide contextual suggestions
    if (term.match(/^\d{1,3}\./)) {
      // IP address pattern
      suggestions.push({
        type: 'ip',
        icon: <IPIcon />,
        primary: `Search for IP: ${term}*`,
        secondary: 'Search by IP address',
        value: term
      });
    }

    if (term.match(/^([0-9A-Fa-f]{2}[:-]?){1,6}/)) {
      // MAC address pattern
      suggestions.push({
        type: 'mac',
        icon: <MACIcon />,
        primary: `Search for MAC: ${term}*`,
        secondary: 'Search by MAC address',
        value: term
      });
    }

    if (term.length > 2) {
      // General suggestions
      suggestions.push({
        type: 'device',
        icon: <DeviceIcon />,
        primary: `Devices containing "${term}"`,
        secondary: 'Search device names',
        value: term
      });

      suggestions.push({
        type: 'location',
        icon: <LocationIcon />,
        primary: `Locations containing "${term}"`,
        secondary: 'Search location names',
        value: term
      });
    }

    setSuggestions(suggestions);
  };

  const handleClear = () => {
    setLocalSearch('');
    setSuggestions([]);
    setShowResults(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleHistoryClick = (searchTerm) => {
    setLocalSearch(searchTerm);
    setShowResults(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setLocalSearch(suggestion.value);
    setShowResults(false);
  };

  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('deviceSearch.history');
    if (onClearRecentSearches) {
      onClearRecentSearches();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowResults(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
      <TextField
        ref={searchInputRef}
        fullWidth
        variant="outlined"
        placeholder={placeholder}
        value={localSearch}
        onChange={(e) => setLocalSearch(e.target.value)}
        onFocus={() => setShowResults(true)}
        onBlur={() => {
          setTimeout(() => setShowResults(false), 200);
          // Save to history on blur if there's a search term
          if (localSearch.trim()) {
            saveToHistory(localSearch.trim());
          }
        }}
        onKeyDown={handleKeyDown}
        autoFocus={autoFocus}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <SearchIcon color="action" />
              )}
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {localSearch && (
                <Tooltip title="Clear search">
                  <IconButton onClick={handleClear} size="small" edge="end">
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              )}
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: 'primary.main',
            }
          }
        }}
      />

      {/* Result count display */}
      {localSearch && (
        <Fade in={true}>
          <Box
            sx={{
              position: 'absolute',
              bottom: -25,
              left: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography variant="caption" color="textSecondary">
              {isLoading ? (
                'Searching...'
              ) : (
                <>
                  Found <strong>{filteredCount}</strong> of {totalDevices} devices
                </>
              )}
            </Typography>
            {filteredCount > 0 && filteredCount < totalDevices && (
              <Chip
                label={`${Math.round((filteredCount / totalDevices) * 100)}%`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </Fade>
      )}

      {/* Search suggestions and history dropdown */}
      {showResults && (localSearch || searchHistory.length > 0) && (
        <Fade in={true}>
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 0.5,
              maxHeight: 300,
              overflow: 'auto',
              zIndex: 1000
            }}
          >
            <List dense>
              {/* Suggestions */}
              {suggestions.length > 0 && (
                <>
                  <ListItem disabled>
                    <ListItemText
                      primary={
                        <Typography variant="caption" color="textSecondary">
                          Suggestions
                        </Typography>
                      }
                    />
                  </ListItem>
                  {suggestions.map((suggestion, index) => (
                    <ListItem
                      key={index}
                      button
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <ListItemIcon>{suggestion.icon}</ListItemIcon>
                      <ListItemText
                        primary={suggestion.primary}
                        secondary={suggestion.secondary}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </>
              )}

              {/* Search history */}
              {!localSearch && searchHistory.length > 0 && (
                <>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Typography variant="caption" color="textSecondary">
                            Recent Searches
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={clearHistory}
                            sx={{ p: 0.5 }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    />
                  </ListItem>
                  {searchHistory.map((term, index) => (
                    <ListItem
                      key={index}
                      button
                      onClick={() => handleHistoryClick(term)}
                    >
                      <ListItemIcon>
                        <HistoryIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={term}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </>
              )}
            </List>
          </Paper>
        </Fade>
      )}
    </Box>
  );
});

UnifiedSearch.displayName = 'UnifiedSearch';

export default UnifiedSearch;